import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { resolveImageSrc } from '../../image-src';
import { useCmsEnterpriseId } from '../cms-enterprise-context';
import { useAssetPicker } from './asset-picker-control';
import { LibraryUrlTabs } from './library-url-tabs';

type AssetPickerFieldProps = {
  field: { label?: string };
  name: string;
  // Saved page JSON is unvalidated, so a persisted value may be `null` (e.g. a field that was
  // never set, or cleared by hand in stored JSON) rather than an empty string.
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
  hideLabel?: boolean;
};

/**
 * Puck custom field for picking an image through Library-first tabs with URL as an escape hatch.
 * The picker chunk remains behind `./asset-picker-control`'s lazy boundary, shared with
 * `background-field.tsx`; this component owns only the string-value contract, label, and preview.
 *
 * `enterpriseId` is read from `CmsEnterpriseContext` rather than `useRouter()`/
 * `next/compat/router`: a router hook that isn't wrapped by the right route silently returns
 * `null`/an empty string, while the context has an explicit `undefined` default and a single,
 * obvious place (`CmsEnterpriseProvider`) that must wrap the editor tree to supply a real value.
 * That fails more loudly (visibly missing "Browse library" button, not a router that quietly
 * never resolved) if a new editor route forgets to wire up the provider.
 *
 * Upload is intentionally not wired up here (admin-only per ECOMM-12193): omitting `onUpload` in
 * `asset-picker-modal.tsx` makes the shared modal hide its upload/drag-and-drop affordance
 * entirely.
 */
export function AssetPickerField({
  field,
  name,
  value,
  onChange,
  readOnly,
  hideLabel = false,
}: AssetPickerFieldProps): JSX.Element {
  const enterpriseId = useCmsEnterpriseId();
  // Saved page JSON is unvalidated, so `value` may be `null`/`undefined`. A controlled `<input>`
  // whose `value` prop is `null` is treated by React as uncontrolled, which warns loudly the
  // moment the field is edited ("changing an uncontrolled input to be controlled") and can flip
  // the input into a genuinely different mode -- coerce to `''` up front instead.
  const resolvedValue = value ?? '';
  const { handleOpen, modal } = useAssetPicker(enterpriseId, onChange, undefined, readOnly);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(e.currentTarget.value), [
    onChange,
  ]);

  const picker = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {resolvedValue && (
          <img
            src={resolveImageSrc(resolvedValue)}
            alt=''
            data-testid={`asset-picker-preview-${name}`}
            style={{
              width: 64,
              height: 64,
              objectFit: 'cover',
              borderRadius: 4,
              border: '1px solid #d0d4d8',
            }}
          />
        )}
        <LibraryUrlTabs
          enterpriseId={enterpriseId}
          fieldLabel={field.label ?? name}
          name={name}
          value={resolvedValue}
          onBrowseLibrary={handleOpen}
          onUrlChange={handleTextChange}
          readOnly={readOnly}
          browseTestId={`asset-picker-browse-${name}`}
          inputTestId={`asset-picker-input-${name}`}
        />
      </div>

      {modal}
    </>
  );

  return hideLabel ? (
    picker
  ) : (
    <FieldLabel label={field.label ?? name} readOnly={readOnly} el='div'>
      {picker}
    </FieldLabel>
  );
}
